import { ChangeEventHandler, useContext, useMemo, useState } from 'react';
import { Button, Modal, Icon } from 'semantic-ui-react';
import { enumUploadStatus, uploadOptions } from '../../../customTypes/stringLiterals';
import { ButtonComponent } from '../../inputs/buttonComponent/ButtonComponent';
import DropdownLutH2O from '../../inputs/dropdownLut/DropdownLut';
import Wave from "../../../images/modalWave.svg"
import Wave2 from "../../../images/modalWave2.svg";
import './ExcelUploadModal.scss'
import { useAuth } from 'react-oidc-context';
import { QueueService } from '../../../services/QueueService';
import { LookupsContext } from '../../../lookups/LookupFactory';

export type ExcelUploadModalProps = {
    refreshUploadList: () => void;
    selectedJurisdictionId: number;
}

export const ExcelUploadModal = (props: ExcelUploadModalProps) => {
    const [lookups] = useContext(LookupsContext);
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<enumUploadStatus>(enumUploadStatus.Ready);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [errorMessageUploadType, setErrorMessageUploadType] = useState<boolean>(false);
    const [fileUploaded, setFileUploaded] = useState<uploadOptions>(uploadOptions.Waiting);
    const [lutQueueTypeId, setLutQueueTypeId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const auth = useAuth();
    const queueService = useMemo(() => new QueueService(auth.user?.access_token), [auth]);

    const clearErrors = () => {
        setError(enumUploadStatus.Ready);
        setErrorMessage("");
        setErrorMessageUploadType(false);
        setFileUploaded(uploadOptions.Waiting);
    }

    const close = () => {
        setOpen(false);
        setFile(null);
        setFileUploaded(uploadOptions.Waiting);
        setError(enumUploadStatus.Ready);
        setErrorMessage("");
        setLutQueueTypeId(null);
        setLoading(false);
    }

    const handleFileSelect: ChangeEventHandler<HTMLInputElement> = (evt) => {
        let file = evt.target.files[0];

        if (file) {
            if (file.size > 1024 * 1024 * 50) {
                setError(enumUploadStatus.FileTooLarge);
            } else {
                setError(enumUploadStatus.Ready);
                setFile(file);
            }
        }
    };

    const uploadSelectedFile = () => {
        if (file && lutQueueTypeId) {
            setLoading(true);
            queueService.newBulkUpload(props.selectedJurisdictionId, lutQueueTypeId, file)
                .then(() => {
                    clearErrors();
                    setFileUploaded(uploadOptions.Success);
                    props.refreshUploadList();
                    setTimeout(() => close(), 4000); // Close after 4 seconds so they can read the message to check the table
                })
                .catch((e) => {
                    console.error("Error on upload", e);
                    setErrorMessage("Internal Error on Upload");
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setError(enumUploadStatus.SelectFile);
            setErrorMessageUploadType(true);
        };
    };

    return (
        <Modal className="excel-upload-modal-component"
            onClose={() => close()}
            onOpen={() => setOpen(true)}
            open={open}
            trigger={<div className="upload-btn"><Icon className="upload-icon" name="cloud upload" />&nbsp;&nbsp;UPLOAD PROJECTS</div>}
        >
            <Modal.Header>
                <div className="excel-upload-modal-title">Upload Excel File Here</div>
                <div className="wave-div">
                    <img src={Wave} alt="wave" className="wave" />
                </div>
            </Modal.Header>
            <Modal.Content>
                <Modal.Description>
                    <div className="inner-wrapper">
                        <div className="uploader mb-1">

                            {
                                error === enumUploadStatus.SelectFile &&
                                <span className="text-danger ml-2 mb-1">***Please select a file to upload</span>
                            }
                            {
                                error === enumUploadStatus.FileTooLarge &&
                                <span className="text-danger ml-2 mb-1">***File is too large. Please use files under 20 MB.</span>
                            }
                            {
                                errorMessageUploadType &&
                                <span className="text-danger ml-2 mb-1">***Please select an excel upload type</span>
                            }

                            <div className="file-upload">
                                <div className="file-select">
                                    <div className="file-select-button" id="fileName">Choose File</div>
                                    <div className="file-select-name" id="chooseFile">{file?.name ?? "No file chosen..."}</div>
                                    <input type="file" accept=".xlsx" name="chooseFile" id="chooseFile" onChange={handleFileSelect} />
                                </div>
                            </div>
                            <div className='mt-1 excel-dropdown-div'>
                                <DropdownLutH2O
                                    dropdownItems={lookups.lutQueueType}
                                    dropdownTitle={"Please select excel upload type"}
                                    isMultiselect={false}
                                    nullLabel="Select upload type"
                                    onSelect={(_e, data) => setLutQueueTypeId(data.value)}
                                />
                            </div>

                        </div>
                    </div>

                </Modal.Description>
            </Modal.Content>

            <div className="action-div">
                <div className="wave-div">
                    <img src={Wave2} alt="wave" className="wave" />
                </div>
                {fileUploaded === uploadOptions.Success &&
                    <Modal.Actions className="pdf-modal-success-actions">
                        <div className="pr-1">
                            Upload Completed - Please check 'Recent Bulk Uploads' Table for Status and Errors.
                        </div>
                        <div>
                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                                <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </svg>
                        </div>
                        <Button color="grey" onClick={close}>Close</Button>
                    </Modal.Actions>
                }
                {fileUploaded === uploadOptions.Failed &&
                    <div className="error-container">
                        <div className="error-div text-danger">
                            ***There was an issue with your upload***
                            <div className="additional-info-error-text">
                                <br /><b>Additional Info: </b>
                                {errorMessage}
                            </div>
                        </div>
                        <Modal.Actions className="pdf-modal-actions">
                            <ButtonComponent
                                onBtnClick={uploadSelectedFile}
                                label="Try Again"
                                theme={"primaryBtn"}
                            />
                            <Button color="grey" onClick={close}>Cancel</Button>
                        </Modal.Actions>
                    </div>
                }
                {(fileUploaded === uploadOptions.Waiting || fileUploaded === uploadOptions.PDFExpected || fileUploaded === uploadOptions.ShapeExpected) &&
                    <Modal.Actions className="pdf-modal-actions">
                        <ButtonComponent
                            onBtnClick={uploadSelectedFile}
                            label="Upload"
                            disabled={error !== enumUploadStatus.Ready || !file || !lutQueueTypeId || !(file.name.toLowerCase().endsWith(".xlsx"))}
                            loading={loading}
                            theme={"primaryBtn"}
                        />
                        <Button color="grey" onClick={close}>Cancel</Button>
                    </Modal.Actions>
                }
            </div>
        </Modal>
    )
}
